import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover/index.js";
import { cn } from "../../../lib/utils.js";
import {
  COUNTRY_DIAL_CODES,
  COUNTRY_NAMES_HE,
  type CountryDialCode,
} from "../template-management/TemplateCreationWizard/country-dial-codes.js";

export interface AddPhoneCountryPhoneFieldProps {
  countryCallingCode: string;
  nationalNumber: string;
  onCountryCallingCodeChange: (digits: string) => void;
  onNationalNumberChange: (digits: string) => void;
  disabled?: boolean;
}

function flagEmoji(iso: string): string {
  return iso
    .toUpperCase()
    .replace(/[A-Z]/g, (letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)));
}

function digitsOfDialCode(dialCode: string): string {
  return dialCode.replace(/\D/g, "");
}

export function AddPhoneCountryPhoneField({
  countryCallingCode,
  nationalNumber,
  onCountryCallingCodeChange,
  onNationalNumberChange,
  disabled = false,
}: AddPhoneCountryPhoneFieldProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const isHebrewUi = i18n.language === "he";
  const [prefixOpen, setPrefixOpen] = useState(false);
  const [search, setSearch] = useState("");
  const numberRef = useRef<HTMLInputElement>(null);
  const selectedCountry =
    COUNTRY_DIAL_CODES.find((country) => digitsOfDialCode(country.dialCode) === countryCallingCode) ??
    COUNTRY_DIAL_CODES.find((country) => country.code === "IL");
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return COUNTRY_DIAL_CODES;
    return COUNTRY_DIAL_CODES.filter((country) => {
      const hebrewName = COUNTRY_NAMES_HE[country.code] ?? "";
      return (
        country.name.toLowerCase().includes(query) ||
        hebrewName.toLowerCase().includes(query) ||
        country.dialCode.toLowerCase().includes(query) ||
        digitsOfDialCode(country.dialCode).includes(query) ||
        country.code.toLowerCase().includes(query)
      );
    });
  }, [search]);

  function selectCountry(country: CountryDialCode): void {
    setPrefixOpen(false);
    setSearch("");
    onCountryCallingCodeChange(digitsOfDialCode(country.dialCode));
    requestAnimationFrame(() => numberRef.current?.focus());
  }

  return (
    <div
      data-testid="add-phone-country-phone"
      dir="ltr"
      className="overflow-hidden rounded-lg border border-line bg-surface"
    >
      <div className="grid grid-cols-1 min-[24rem]:grid-cols-[8.75rem_minmax(0,1fr)]">
      <label className="grid text-start min-[24rem]:border-e min-[24rem]:border-line" data-testid="add-phone-country-selector">
        <span className="sr-only">{t("whatsapp.addPhone.fields.country")}</span>
        <Popover
          open={prefixOpen}
          onOpenChange={(open) => {
            if (disabled) return;
            setPrefixOpen(open);
            if (!open) setSearch("");
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className="flex h-11 w-full items-center justify-between gap-2 bg-transparent px-3 text-start text-sm disabled:opacity-70"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span aria-hidden="true">{selectedCountry ? flagEmoji(selectedCountry.code) : ""}</span>
                <span className="min-w-0 truncate tabular-nums [direction:ltr]" dir="ltr">
                  {selectedCountry
                    ? `${selectedCountry.code} ${selectedCountry.dialCode}`
                    : `+${countryCallingCode}`}
                </span>
              </span>
              <ChevronDown size={14} className="shrink-0 opacity-60" aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[min(22rem,calc(100vw-2rem))] overflow-hidden p-0">
            <div className="flex items-center gap-2 border-b border-line px-2.5 py-2 focus-within:bg-surface-muted">
              <Search size={14} className="shrink-0 text-ink-muted" aria-hidden="true" />
              <input
                className="min-w-0 flex-1 border-0 bg-transparent text-sm shadow-none ring-0 outline-none focus:border-0 focus:shadow-none focus:ring-0 focus:outline-none focus-visible:border-0 focus-visible:shadow-none focus-visible:ring-0 focus-visible:outline-none"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("whatsapp.addPhone.fields.countrySearch")}
              />
            </div>
            <div
              className="max-h-[17.5rem] overflow-y-auto overscroll-contain p-1"
              onWheel={(event) => event.stopPropagation()}
            >
              {filtered.map((country) => {
                const isSelected = country.code === selectedCountry?.code;
                const hebrewName = isHebrewUi ? COUNTRY_NAMES_HE[country.code] : undefined;
                return (
                  <button
                    key={country.code}
                    type="button"
                    className={cn(
                      "flex w-full min-w-0 items-center gap-2.5 overflow-hidden rounded-md px-2 py-1.5 text-start hover:bg-surface-muted",
                      isSelected ? "bg-surface-muted" : null,
                    )}
                    onClick={() => selectCountry(country)}
                  >
                    <span aria-hidden="true">{flagEmoji(country.code)}</span>
                    <span className="grid min-w-0 flex-1 gap-0.5">
                      <span className="truncate text-sm text-ink">{country.name}</span>
                      {hebrewName ? (
                        <span className="truncate text-[0.7rem] leading-tight text-ink-muted">
                          {hebrewName}
                        </span>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-sm tabular-nums text-ink-muted [direction:ltr]" dir="ltr">
                      {country.dialCode}
                    </span>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </label>

      <label className="grid border-t border-line text-start min-[24rem]:border-t-0">
        <span className="sr-only">{t("whatsapp.addPhone.fields.phoneNumber")}</span>
        <input
          ref={numberRef}
          data-testid="add-phone-national-number"
          dir="ltr"
          inputMode="tel"
          disabled={disabled}
          aria-label={t("whatsapp.addPhone.fields.phoneNumber")}
          className="h-11 w-full border-0 bg-transparent px-3 font-mono text-sm tabular-nums outline-none [unicode-bidi:isolate] disabled:opacity-70"
          value={nationalNumber}
          placeholder={t("whatsapp.addPhone.fields.phoneNumberPlaceholder")}
          onChange={(event) => onNationalNumberChange(event.target.value.replace(/\D/g, ""))}
        />
      </label>
      </div>
    </div>
  );
}
